import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Shield, Users, DollarSign, Trophy, CheckCircle, Star, Zap } from 'lucide-react';

const AboutPage: React.FC = () => {
  const navigate = useNavigate();

  const handleViewDashboard = () => {
    // Check if user is authenticated (you can implement this check)
    // For now, always redirect to auth first
    navigate('/auth');
  };

  const features = [
    {
      icon: '💰',
      title: 'Dual Income Streams',
      description: 'Earn from both racing purses and SUI staking rewards. Current members see 15.7% APY.',
      details: [
        'Racing purses from horse victories',
        'SUI staking rewards (15.7% APY)',
        'Breeding program revenue',
        'NFT trading commissions'
      ]
    },
    {
      icon: '🏇',
      title: 'Real Horse Ownership',
      description: 'Own actual champion thoroughbreds as NFTs with complete provenance and performance data.',
      details: [
        'Champion thoroughbred ownership',
        'Complete performance tracking',
        'Breeding rights and lineage',
        'Insurance and legal protection'
      ]
    },
    {
      icon: '🗳️',
      title: 'Community Governed',
      description: 'Vote on horse acquisitions, breeding programs, and treasury management through DAO.',
      details: [
        'Vote on horse acquisitions',
        'Breeding program decisions',
        'Treasury allocation choices',
        'Strategic partnership votes'
      ]
    },
    {
      icon: '🔒',
      title: 'Institutional Security',
      description: 'Multi-sig treasury, insurance coverage, and legal compliance exceed traditional funds.',
      details: [
        'Multi-signature treasury security',
        'Comprehensive insurance coverage',
        'Legal compliance framework',
        'Regular security audits'
      ]
    },
  ];

  const benefits = [
    {
      title: 'Proven Track Record',
      description: 'Our horses have won over $2.5M in purses',
      icon: Trophy,
      color: 'text-yellow-400'
    },
    {
      title: 'Transparent Operations',
      description: 'All transactions and decisions are on-chain',
      icon: Shield,
      color: 'text-green-400'
    },
    {
      title: 'Community Driven',
      description: 'Members vote on all major decisions',
      icon: Users,
      color: 'text-blue-400'
    },
    {
      title: 'High Returns',
      description: '15.7% APY from staking + racing winnings',
      icon: TrendingUp,
      color: 'text-cyan-400'
    }
  ];

  const stats = [
    { label: 'Treasury Value', value: '7.2M SUI', icon: DollarSign },
    { label: 'Current APY', value: '15.7%', icon: TrendingUp },
    { label: 'Champion Horses', value: '12', icon: Trophy },
    { label: 'Active Members', value: '247', icon: Users },
  ];

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
          <h1 className="text-4xl font-bold text-white mb-4">About SUI-ndicate</h1>
          <p className="text-xl text-slate-300 max-w-3xl">
            The world's first blockchain-based thoroughbred racing syndicate, combining 
            traditional horse racing with cutting-edge DeFi technology.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <div key={index} className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
              <div className="flex items-center justify-center mb-2">
                <stat.icon className="w-8 h-8 text-cyan-400" />
              </div>
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className="text-slate-400 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Features Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">How It Works</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-slate-300 mb-6">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-center space-x-2 text-slate-400">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Why Choose SUI-ndicate?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6 text-center">
                <benefit.icon className={`w-12 h-12 ${benefit.color} mx-auto mb-4`} />
                <h3 className="text-lg font-bold text-white mb-2">{benefit.title}</h3>
                <p className="text-slate-400 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Technology Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Built on Sui Blockchain</h2>
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Lightning Fast</h3>
                <p className="text-slate-400 text-sm">Sui's parallel execution enables instant transactions</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Secure</h3>
                <p className="text-slate-400 text-sm">Move language provides enhanced security guarantees</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Scalable</h3>
                <p className="text-slate-400 text-sm">Horizontal scaling for unlimited growth potential</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Join?</h2>
          <p className="text-xl text-slate-300 mb-8">
            Connect your wallet and start earning from the future of horse racing
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/auth"
              className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-4 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 flex items-center justify-center space-x-2"
            >
              <span>Join Syndicate</span>
              <TrendingUp className="w-5 h-5" />
            </Link>
            <button
              onClick={handleViewDashboard}
              className="border border-cyan-500 text-cyan-400 px-8 py-4 rounded-lg hover:bg-cyan-500/10 transition-all duration-200"
            >
              View Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
